/**
 * What a person asked one-shot for, carried from the describe dialog to the editor — Story 27.4.
 *
 * `funnels-list`'s **Draft my funnel** opens `editor-build` on an empty canvas, and the dashboard
 * navigates by loading the page, so the two answers travel in `sessionStorage`: this tab only,
 * gone with it, and never in a URL, where a description that names a person would reach the
 * history and any proxy's log. It stays until the proposal is accepted or discarded, so a reload
 * asks again — and the same description comes back from the org's cache, uncounted (Story 27.3).
 *
 * Every read and write is guarded: storage can be off, full or cleared, and a page that cannot
 * read it sends the person back to Funnels rather than failing.
 */
import { ARCHETYPES, type Archetype } from '@leadgible/schema'

export type ProposalRequest = {
  /** "What does your business do?" */
  readonly business: string
  /** "Who do you want to hear from?" */
  readonly customer: string
  /** The kind "Draft it again as" asked for; absent on a first draft. */
  readonly archetype?: Archetype
}

const KEY = 'leadgible.proposal'

/**
 * Where a proposal is drafted and reviewed: the editor, before its funnel exists. `new` can never
 * be a funnel's id — those are `fnl_` and 26 Crockford characters — so the editor's own route
 * serves it (`editorPath` reads it as the funnel id, and the editor sees which).
 */
export const PROPOSING = 'new'
export const PROPOSAL_PATH = `/funnels/${PROPOSING}`

/** Whether the request was kept — false when storage refused it. */
export function keepRequest(request: ProposalRequest): boolean {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(request))
    return true
  } catch {
    return false
  }
}

export function readRequest(): ProposalRequest | null {
  try {
    const raw = window.sessionStorage.getItem(KEY)
    if (raw === null) return null
    const value = JSON.parse(raw) as Record<string, unknown>
    if (typeof value['business'] !== 'string' || typeof value['customer'] !== 'string') return null
    const archetype = value['archetype']
    return {
      business: value['business'],
      customer: value['customer'],
      ...(typeof archetype === 'string' && (ARCHETYPES as readonly string[]).includes(archetype)
        ? { archetype: archetype as Archetype }
        : {}),
    }
  } catch {
    return null
  }
}

export function forgetRequest(): void {
  try {
    window.sessionStorage.removeItem(KEY)
  } catch {
    // Nothing to forget where nothing could be kept.
  }
}

/**
 * The two answers as the one description the route takes (`{ description }`) — each ended as a
 * sentence and joined, so "…site plans for commercial builds" and "Developers and general
 * contractors…" read as two sentences and not one run-on. Nothing else about the words changes:
 * the server trims and folds the whitespace (`readDescription`), and since Story 27.6 the result
 * is the funnel's ideal customer, in the person's own words.
 */
export function describedAs(business: string, customer: string): string {
  const sentence = (text: string) => {
    const trimmed = text.trim()
    return trimmed === '' || /[.!?…]["”')]*$/.test(trimmed) ? trimmed : `${trimmed}.`
  }
  return [sentence(business), sentence(customer)].filter((part) => part !== '').join(' ')
}
